import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
axios.defaults.withCredentials = true;
export const API = `${BACKEND_URL}/api`;

// Products
export const getProducts = async (category = null, collection = null, search = null) => {
  const params = {};
  if (category && category !== 'Все') params.category = category;
  if (collection && collection !== 'Все') params.collection = collection;
  if (search) params.search = search;
  const response = await axios.get(`${API}/products`, { params });
  return response.data;
};

export const getProduct = async (article) => {
  const response = await axios.get(`${API}/products/${article}`);
  return response.data;
};

export const getCategories = async () => {
  const response = await axios.get(`${API}/categories`);
  return response.data;
};

export const getCollections = async () => {
  try {
    const response = await axios.get(`${API}/collections`);
    return response.data;
  } catch (error) {
    return [];
  }
};

// Orders
export const createOrder = async (orderData) => {
  const response = await axios.post(`${API}/orders`, orderData);
  return response.data;
};

export const getUserOrders = async (userId) => {
  const response = await axios.get(`${API}/users/${userId}/orders`);
  return response.data;
};

// Promo codes
export const validatePromoCode = async (code, cartTotal = 0) => {
  const response = await axios.post(`${API}/promo/validate`, null, { params: { code, cart_total: cartTotal } });
  return response.data;
};

export const getDeliveryOptions = async () => {
  const response = await axios.get(`${API}/delivery-options`);
  return response.data;
};

// Auth
export const registerUser = async (userData) => {
  const response = await axios.post(`${API}/auth/register`, userData);
  return response.data;
};

export const loginUser = async (credentials) => {
  const response = await axios.post(`${API}/auth/login`, credentials);
  return response.data;
};

export const getUser = async (userId) => {
  const response = await axios.get(`${API}/users/${userId}`);
  return response.data;
};

export const toggleFavorite = async (userId, productId) => {
  const response = await axios.post(`${API}/users/${userId}/favorites/${productId}`);
  return response.data;
};

// File Upload
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const uploadMultipleFiles = async (files) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  const response = await axios.post(`${API}/upload/multiple`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Banners
export const getBanners = async () => {
  const response = await axios.get(`${API}/banners`);
  return response.data;
};

// Admin API with basic auth
const getAdminAuth = () => {
  const creds = localStorage.getItem('admin_credentials');
  if (!creds) return {};
  return { auth: JSON.parse(creds) };
};

export const adminLogin = async (username, password) => {
  try {
    const response = await axios.get(`${API}/admin/stats`, {
      auth: { username, password }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || 'Ошибка авторизации' };
  }
};

export const getAdminStats = async () => {
  const response = await axios.get(`${API}/admin/stats`, getAdminAuth());
  return response.data;
};

export const getAdminOrders = async (status = 'all') => {
  const params = status !== 'all' ? { status } : {};
  const response = await axios.get(`${API}/admin/orders`, { ...getAdminAuth(), params });
  return response.data;
};

export const updateOrderStatus = async (orderId, status) => {
  const response = await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, {}, getAdminAuth());
  return response.data;
};

export const verifyPickupCode = async (code) => {
  const response = await axios.get(`${API}/admin/pickup/verify`, { ...getAdminAuth(), params: { code } });
  return response.data;
};

export const issuePickupOrder = async (code) => {
  const response = await axios.post(`${API}/admin/pickup/issue`, { code }, getAdminAuth());
  return response.data;
};

export const getAdminProducts = async () => {
  const response = await axios.get(`${API}/products?active_only=false`);
  return response.data;
};

export const createProduct = async (productData) => {
  const response = await axios.post(`${API}/admin/products`, productData, getAdminAuth());
  return response.data;
};

export const updateProduct = async (productId, productData) => {
  const response = await axios.put(`${API}/admin/products/${productId}`, productData, getAdminAuth());
  return response.data;
};

export const deleteProduct = async (productId) => {
  const response = await axios.delete(`${API}/admin/products/${productId}`, getAdminAuth());
  return response.data;
};

export const getAdminPromoCodes = async () => {
  const response = await axios.get(`${API}/admin/promo-codes`, getAdminAuth());
  return response.data;
};

export const createPromoCode = async (promoData) => {
  const response = await axios.post(`${API}/admin/promo-codes`, promoData, getAdminAuth());
  return response.data;
};

export const deletePromoCode = async (promoId) => {
  const response = await axios.delete(`${API}/admin/promo-codes/${promoId}`, getAdminAuth());
  return response.data;
};

export const getAdminDeliveryOptions = async () => {
  const response = await axios.get(`${API}/admin/delivery-options`, getAdminAuth());
  return response.data;
};

export const createDeliveryOption = async (option) => {
  const response = await axios.post(`${API}/admin/delivery-options`, option, getAdminAuth());
  return response.data;
};

export const updateDeliveryOption = async (optionId, option) => {
  const response = await axios.put(`${API}/admin/delivery-options/${optionId}`, option, getAdminAuth());
  return response.data;
};

export const updateUserCoins = async (userEmail, amount, action) => {
  const response = await axios.post(`${API}/admin/users/coins`, {
    user_email: userEmail,
    amount,
    action
  }, getAdminAuth());
  return response.data;
};

export const getAdminUsers = async () => {
  const response = await axios.get(`${API}/admin/users`, getAdminAuth());
  return response.data;
};

// Admin Banners
export const getAdminBanners = async () => {
  const response = await axios.get(`${API}/admin/banners`, getAdminAuth());
  return response.data;
};

export const createBanner = async (bannerData) => {
  const response = await axios.post(`${API}/admin/banners`, bannerData, getAdminAuth());
  return response.data;
};

export const updateBanner = async (bannerId, bannerData) => {
  const response = await axios.put(`${API}/admin/banners/${bannerId}`, bannerData, getAdminAuth());
  return response.data;
};

export const deleteBanner = async (bannerId) => {
  const response = await axios.delete(`${API}/admin/banners/${bannerId}`, getAdminAuth());
  return response.data;
};

export const logoutUser = async () => axios.post(`${API}/auth/logout`);

export const verifyRegistration = async (payload) => (await axios.post(`${API}/auth/register/verify`, payload)).data;
